import React, { useEffect, useState } from "react";
import {
  Button,
  Drawer,
  Row,
  Col,
  Typography,
  Divider,
  Input,
  Switch,
  Select,
  Form,
  message,
  InputNumber,
  Image,
  Upload,
  Spin,
  Modal,
  Table,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Product } from "../../../../types/product";
import { CREATE_PRODUCT, UPDATE_PRODUCT, DELETE_MANY_VARIANTS, GET_PRODUCTS } from "./requests";
import { useMutation, useQuery } from "@apollo/client";
import { productTypes } from "../../../../utils/constants";
import { productTypes as typesProducts } from "../../../../types";
import VariantForm from "../variantForm";
import ExtrasForm from "../extrasModal";
import useUpload from "../../../../hooks/use-upload";

const { Title, Text } = Typography;

interface EditDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  product: Product | null;
  setProduct: (product: Product | null) => void;
  refetch: () => void;
  isCreateView: boolean;
}

const EditDrawer: React.FC<EditDrawerProps> = ({ open, setOpen, product, setProduct, refetch, isCreateView }) => {
  const [form] = Form.useForm();
  const { upload } = useUpload();
  const [hasVariants, setHasVariants] = useState<boolean>(
    product?.variants ? product.variants.length > 1 : false,
  );
  const [hasExtras, setHasExtras] = useState<boolean | undefined>(false);
  const [requireBeverages, setRequireBeverages] = useState<boolean | undefined>(false);
  const [requireComplements, setRequireComplements] = useState<boolean | undefined>(false);
  const [hasBeverages, setHasBeverages] = useState<boolean | undefined>(false);
  const [hasComplements, setHasComplements] = useState<boolean | undefined>(false);
  const [maxBeverages, setMaxBeverages] = useState<number | undefined>(0);
  const [maxComplements, setMaxComplements] = useState<number | undefined>(0);
  const [price, setPrice] = useState<number | undefined>(0);
  const [productVariants, setProductVariants] = useState<typeof typesProducts.productVariant[] | null>([]);
  const [productType, setProductType] = useState<string>("");
  const [selectedProductsExtra, setSelectedProductsExtra] = useState<{
    product: string;
    max: number;
    required: boolean;
  }[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadLoding, setUploadLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSwitch, setLoadingSwitch] = useState(false);
  const [localConfirmText, setLocalConfirmText] = useState<string>("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedComplements, setSelectedComplements] = useState<string[]>([]);
  const [selectedBeverages, setSelectedBeverages] = useState<string[]>([]);
  const [pageConfigBeverages, setPageConfigBeverages] = useState({ page: 1,
    pageSize: 5 });
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 5 });
  const [recommendedProduct, setRecommendedProduct] = useState(false);

  const [createProductMutation] = useMutation(CREATE_PRODUCT);
  const [updateProductMutation] = useMutation(UPDATE_PRODUCT);
  const [deleteManyVariantsMutation] = useMutation(DELETE_MANY_VARIANTS);

  const { data: productsBeverages, loading: loadingProductsBeverages } = useQuery(GET_PRODUCTS, {
    variables: {
      params: pageConfigBeverages,
      type: "beverage",
    },
  });

  const { data: productsComplements, loading: loadingProductsComplements } = useQuery(GET_PRODUCTS, {
    variables: {
      params: pageConfig,
      type: "complement",
    },
  });

  useEffect(() => {
    if (product) {
      const newInitialValues = {
        name: product?.name || "",
        description: product?.description || "",
        type: product?.type || "",
        img: product?.variants?.[0]?.img || "",
        price: product?.variants?.length === 1 ? product?.variants[0].price : undefined,
        variants:
          product.variants?.length > 1
            ? product?.variants.map((variant) => ({
              id: variant.id,
              title: variant.title || "",
              price: variant.price,
              description: variant.description || "",
            }))
            : [],
      };

      setProductVariants(product.variants.map(variant => ({
        id: variant.id,
        title: variant.title || "",
        price: variant.price || 0,
        img: variant.img,
        description: variant.description || "",
        isPrimaryVariant: variant.isPrimaryVariant,
      })));

      const primaryVariant = product.variants.find(variant => variant.isPrimaryVariant);
      setImageUrl(primaryVariant?.img || null);
      setPrice(primaryVariant?.price);

      setProductType(product.type);
      setHasVariants(product.variants.length > 1);
      setRequireComplements(product.rulesAdditionals?.requireComplements);
      setHasComplements(product.rulesAdditionals?.complementItems ? product.rulesAdditionals?.complementItems.length > 0 : false);
      setMaxComplements(product.rulesAdditionals?.maxComplements);
      setRequireBeverages(product.rulesAdditionals?.requireBeverages);
      setHasBeverages(product.rulesAdditionals?.beverageItems ? product.rulesAdditionals?.beverageItems.length > 0 : false);
      setMaxBeverages(product.rulesAdditionals?.maxBeverages);
      setHasExtras(product.rulesAdditionals?.includesExtras);
      setRecommendedProduct(product.recommendedProduct)
      setSelectedProductsExtra(
        product.rulesAdditionals?.extraRules
          ? product.rulesAdditionals.extraRules.map((item) => ({
            product: item.product.id,
            max: item.max ?? 0,
            required: item.required ?? false,
          }))
          : [],
      );

      setSelectedBeverages(
        product.rulesAdditionals?.beverageItems && product.rulesAdditionals?.beverageItems.length > 0 ?
          product.rulesAdditionals.beverageItems.map((item) => item.id) : [],
      );

      setSelectedComplements(
        product.rulesAdditionals?.complementItems && product.rulesAdditionals?.complementItems.length > 0 ?
          product.rulesAdditionals.complementItems.map((item) => item.id) : [],
      );

      form.setFieldsValue(newInitialValues);
    }
  }, [product, form]);

  const onClose = () => {
    setProduct(null);
    form.resetFields();
    setOpen(false);
    setProductType("");
    setImageUrl(null);
    setSelectedBeverages([]);
    setSelectedComplements([]);
    setHasBeverages(false);
    setHasComplements(false);
    setRequireBeverages(false);
    setRequireComplements(false);
  };

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `products/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);
      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen agregada.");
  };

  const handleProductType = (value: string) => {
    setProductType(value);
  };

  const handleSave = () => {
    setLoading(true);
    const updatedProductVariants = productVariants?.map((variant) => {
      const variantCopy = { ...variant };
      delete variantCopy.id;
      return variantCopy;
    });
    form.validateFields().then(async (values) => {
      if (!isCreateView) {
        if(product) {
          const primaryVariant = product.variants.find(variant => variant.isPrimaryVariant);
          await updateProductMutation({ variables:
          { id: product.id,
            product: {
              name: values.name,
              description: values?.description,
              categories: [],
              type: values.type,
              recommendedProduct: recommendedProduct,
              rulesAdditionals: {
                requireComplements: requireComplements,
                maxComplements: maxComplements,
                complementItems: selectedComplements,
                requireBeverages: requireBeverages,
                maxBeverages: maxBeverages,
                beverageItems: selectedBeverages,
                includesExtras: hasExtras,
                extraRules: selectedProductsExtra,
              },
              variants: hasVariants ? updatedProductVariants : {
                price: productType === "extra" ? 0 : Number(price),
                img: imageUrl,
                id: primaryVariant?.id,
              },
            },
          },
          });
        }
      } else {
        await createProductMutation({
          variables: {
            product: {
              name: values.name,
              description: values?.description,
              categories: [],
              type: values.type,
              recommendedProduct: recommendedProduct,
              rulesAdditionals: {
                requireComplements: requireComplements,
                maxComplements: maxComplements,
                complementItems: selectedComplements,
                requireBeverages: requireBeverages,
                maxBeverages: maxBeverages,
                beverageItems: selectedBeverages,
                includesExtras: hasExtras,
                extraRules: selectedProductsExtra,
              },
            },
            productVariants: hasVariants ? updatedProductVariants : {
              price: productType === "extra" ? 0 : Number(price),
              img: imageUrl,
            },
          },
        });
      }
      await refetch();
      message.success("Producto creado con exito");
    }).catch((error) => {
      console.log("Error en el formulario:", error);
      message.error("Hubo un error");
    }).finally(() => {
      onClose();
      setLoading(false);
    });
  };

  const desactiveVariants = async () => {
    try {
      setLoadingSwitch(true);
      setHasVariants(false);

      await deleteManyVariantsMutation({ variables: { product: product?.id } })
      await refetch();

      message.success("Variantes desactivadas exitosamente.");

      const primaryVariant = product?.variants.find(variant => variant.isPrimaryVariant);
      if (primaryVariant) {
        setProductVariants([primaryVariant]);
      } else {
        setProductVariants([]);
      }
    } catch(err) {
      console.log(err)
    } finally {
      setLocalConfirmText("");
      setLoadingSwitch(false);
    }
  };

  const showConfirm = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    desactiveVariants();
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setLocalConfirmText("");
  };

  const columns = [
    {
      title: "Imagen",
      key: "img",
      render: (row: typeof typesProducts.productVariant) => {
        return (
          <Image
            width={50}
            src={row.img}
            alt={row.title}
          />
        );
      },
    },
    {
      title: "Nombre",
      key: "name",
      render: (row: typeof typesProducts.productVariant) => {
        if(row.title){
          return <Text strong>{row.title}</Text>;
        } else {
          return <Text strong>{row?.product?.name}</Text>;
        }
      },
    },
    {
      title: "Descripción",
      key: "description",
      render: (row: typeof typesProducts.productVariant) => {
        if(row.description){
          return <Text strong>{row.description}</Text>;
        } else {
          return <Text strong>{row?.product?.description}</Text>;
        }
      },
    },
  ];

  const rowSelectionComplements = {
    selectedRowKeys: selectedComplements,
    onChange: (selectedRowKeys: React.Key[]) => {
      const validSelectedRowKeys = selectedRowKeys.filter(key => key !== null);
      setSelectedComplements(validSelectedRowKeys as string[]);
    },
    preserveSelectedRowKeys: true,
  };

  const rowSelectionBeverages = {
    selectedRowKeys: selectedBeverages,
    onChange: (selectedRowKeys: React.Key[]) => {
      const validSelectedRowKeys = selectedRowKeys.filter(key => key !== null);
      setSelectedBeverages(validSelectedRowKeys as string[]);
    },
    preserveSelectedRowKeys: true,
  };

  const handleSelectComplements = (record: typeof typesProducts.product) => {
    const selectedProductIds = [...selectedComplements];
    const productId = record.id;

    if (selectedProductIds.includes(productId)) {
      setSelectedComplements(selectedProductIds.filter(id => id !== productId));
    } else {
      setSelectedComplements([...selectedProductIds, productId]);
    }
  };

  const handleSelectBeverages = (record: typeof typesProducts.product) => {
    const selectedProductIds = [...selectedBeverages];
    const productId = record.id;

    if (selectedProductIds.includes(productId)) {
      setSelectedBeverages(selectedProductIds.filter(id => id !== productId));
    } else {
      setSelectedBeverages([...selectedProductIds, productId]);
    }
  };

  return (
    <Drawer title={product ? "Editar Producto" : "Crear Producto"} placement="right" width="50%" onClose={onClose} visible={open}>
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Nombre" name="name">
              <Input placeholder="Nombre del producto" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Descripción" name="description">
              <Input.TextArea placeholder="Descripción del producto" rows={3} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Tipo" name="type">
              <Select
                onChange={(value) => handleProductType(value)}
                placeholder="Seleccione un tipo"
              >
                {Object.entries(productTypes).map(([key, value]) => {
                  return (
                    <Select.Option key={key} value={key}>
                      {value}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
          {!hasVariants && (
            <Col span={24}>
              <Col>
                <Form.Item label="Imagen">
                  <Upload
                    beforeUpload={() => false}
                    listType="picture"
                    maxCount={1}
                    accept="image/*"
                    onChange={(info) => {
                      handleFileUpload(info);
                    }}
                    showUploadList={false}
                  >
                    <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
                    {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
                  </Upload>
                  {imageUrl && (
                    <Image style={{ marginLeft: 20 }} width={90} src={imageUrl} />
                  )}
                </Form.Item>
              </Col>
              {productType !== "extra" && (
                <Col>
                  <Form.Item label="Precio" name="price">
                    <Input
                      type="number"
                      defaultValue={price}
                      placeholder="Precio del producto"
                      onChange={(e) => setPrice(Number(e.target.value))}
                    />
                  </Form.Item>
                </Col>
              )}
            </Col>
          )}
          <Form.Item style={{ marginLeft: 16 }} label="Recomendar Producto" name="recommendedProduct">
            <Switch checked={recommendedProduct} onChange={setRecommendedProduct} />
          </Form.Item>
        </Row>
        <Divider />
        <Col span={24}>
          <Row style={{ justifyContent: "space-between" }}>
            <Title level={4}>Variantes</Title>
            <Switch checked={hasVariants} onChange={(checked) => {
              if (!checked) {
                showConfirm();
              } else {
                setHasVariants(true);
              }
            }} />
          </Row>
        </Col>
        {hasVariants && (
          <VariantForm
            product={product}
            variants={productVariants}
            setVariants={setProductVariants}
            isCreateView={isCreateView}
            refetch={refetch}
          />
        )}
        {productType !== "" && (productType === "combo" || productType === "main") && (
          <>
            <Divider />
            <Col span={24}>
              <Row style={{ justifyContent: "space-between" }}>
                <Title level={4}>Agregar Extras</Title>
                <Switch checked={hasExtras} onChange={setHasExtras} />
              </Row>
            </Col>
          </>
        )}
        {hasExtras && (
          <ExtrasForm
            selectedProducts={selectedProductsExtra}
            setSelectedProducts={setSelectedProductsExtra}
          />
        )}
        {productType !== "" && (productType === "combo" || productType === "main") && (
          <>
            <Divider />
            <Row align="middle" justify="space-between">
              <Col>
                <Title level={4}>Agregar Complementos</Title>
              </Col>
              <Col>
                <Switch checked={hasComplements} onChange={setHasComplements} />
              </Col>
            </Row>

            {hasComplements && (
              <>
                <Title level={5}>Máximo de Complementos</Title>
                <Row>
                  <InputNumber min={0} value={maxComplements} onChange={(value) => setMaxComplements(value ?? 0)} />
                  <Text style={{ marginLeft: 10,
                    marginRight: 5 }} strong>¿Obligatorio?</Text>
                  <Switch checked={requireComplements} onChange={setRequireComplements} />
                </Row>

                <Table
                  rowSelection={rowSelectionComplements}
                  onRow={(record: Product) => ({
                    onClick: () => handleSelectComplements(record),
                  })}
                  rowKey="id"
                  columns={columns}
                  dataSource={productsComplements?.productVariants.results ?? []}
                  loading={loadingProductsComplements}
                  pagination={{
                    current: Number(pageConfig.page),
                    defaultCurrent: pageConfig.page,
                    pageSize: Number(pageConfig.pageSize),
                    defaultPageSize: pageConfig.pageSize,
                    total: productsComplements?.productVariants?.info?.count,
                    showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
                    onChange: (page, pageSize) => {
                      setPageConfig({ page,
                        pageSize });
                    },
                    showSizeChanger: true,
                  }}
                />
              </>
            )}

            <Row align="middle" justify="space-between">
              <Col>
                <Title level={4}>Agregar Bebidas</Title>
              </Col>
              <Col>
                <Switch checked={hasBeverages} onChange={setHasBeverages} />
              </Col>
            </Row>

            {hasBeverages && (
              <>
                <Title level={5}>Máximo de Bebidas</Title>
                <Row>
                  <InputNumber min={0} value={maxBeverages} onChange={(value) => setMaxBeverages(value ?? 0)} />
                  <Text style={{ marginLeft: 10,
                    marginRight: 5 }} strong>¿Obligatorio?</Text>
                  <Switch checked={requireBeverages} onChange={setRequireBeverages} />
                </Row>

                <Table
                  rowSelection={rowSelectionBeverages}
                  onRow={(record: Product) => ({
                    onClick: () => handleSelectBeverages(record),
                  })}
                  rowKey="id"
                  columns={columns}
                  loading={loadingProductsBeverages}
                  dataSource={productsBeverages?.productVariants.results ?? []}
                  pagination={{
                    current: Number(pageConfigBeverages.page),
                    defaultCurrent: pageConfigBeverages.page,
                    pageSize: Number(pageConfigBeverages.pageSize),
                    defaultPageSize: pageConfigBeverages.pageSize,
                    total: productsBeverages?.productVariants?.info?.count,
                    showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
                    onChange: (page, pageSize) => {
                      setPageConfigBeverages({ page,
                        pageSize });
                    },
                    showSizeChanger: true,
                  }}
                />
              </>
            )}
          </>
        )}
        <div style={{ marginTop: 20 }}>
          <Button
            type="primary"
            onClick={handleSave}
            size="large"
            style={{ width: "100%" }}
            disabled={
              productType === "extra"
                ? (!imageUrl)
                : (!hasVariants && !price) ||
                  (!hasVariants && !imageUrl) ||
                  (hasVariants && productVariants?.length === 0)
            }
            loading={loading}
          >
            Guardar
          </Button>
        </div>
      </Form>
      <Modal
        title="¿Estás seguro de desactivar las variantes?"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancelar
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={localConfirmText !== "CONTINUAR"}
            onClick={handleOk}
            loading={loadingSwitch}
          >
            Confirmar
          </Button>,
        ]}
      >
        <p>Si desactivas las variantes, todas serán eliminadas excepto la variante principal. Esta acción no se puede deshacer.</p>
        <p>Para confirmar, escribe <strong>CONTINUAR</strong> en el siguiente campo:</p>
        <Input
          placeholder="Escribe CONTINUAR"
          value={localConfirmText}
          onChange={(e) => {
            setLocalConfirmText(e.target.value);
          }}
        />
      </Modal>
    </Drawer>
  );
};

export default EditDrawer;
